/**
 * Cashflow Analysis Page
 *
 * Main page component for the cashflow analysis feature. Displays monthly cash outflow
 * projections based on PO amounts, supplier payment terms, and tariff rates.
 *
 * Features:
 * - Summary bar chart (stacked by supplier or status)
 * - Monthly comparison table (total / by supplier / by PO views)
 * - Month drill-down detail table
 * - Date range, status, brand, and supplier filtering
 * - Supplier management CRUD dialog
 * - Shared PO creation/editing dialog with cashflow fields
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";

import CashflowService from "services/CashflowService";
import InventoryService, { CatalogSuggestions } from "services/InventoryService";
import {
  Supplier,
  MonthlyBucket,
  CashflowWarning,
} from "models/CashflowModel";
import { PurchaseOrder } from "models/InventoryModel";
import { usePOManagement } from "shared/hooks/usePOManagement";
import { downloadBlob } from "shared/utils/downloadBlob";
import { persistPO } from "shared/utils/persistPO";

import {
  CashflowViewMode,
  StatusFilter,
  PaymentStatusFilter,
  ChartStackingMode,
} from "./types";
import {
  getDefaultStartMonth,
  getDefaultEndMonth,
  getMonthRange,
  getUniqueBrands,
} from "./utils";

import CashflowWarningsSection from "./components/CashflowWarningsSection";
import CashflowControlPanel from "./components/CashflowControlPanel";
import CashflowBarChart from "./components/CashflowBarChart";
import MonthlyComparisonTable from "./components/MonthlyComparisonTable";
import MonthlyDetailTable from "./components/MonthlyDetailTable";
import SupplierManagementDialog from "./components/dialogs/SupplierManagementDialog";
import SavePricesToSkuMasterDialog, { PriceChange, ProductNumberChange, findPriceChanges, findProductNumberChanges } from "shared/components/dialogs/SavePricesToSkuMasterDialog";
import { POManagementDialog, DeletePOConfirmation } from "shared/components/dialogs";

const cashflowService = new CashflowService();
const inventoryService = new InventoryService();

export default function CashflowPage() {
  // ── Date range & filters ──
  const [startMonth, setStartMonth] = useState(getDefaultStartMonth);
  const [endMonth, setEndMonth] = useState(getDefaultEndMonth);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<PaymentStatusFilter>("all");
  const [viewMode, setViewMode] = useState<CashflowViewMode>("byPO");
  const [chartStackingMode, setChartStackingMode] = useState<ChartStackingMode>("bySupplier");
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [selectedSupplierIds, setSelectedSupplierIds] = useState<number[]>([]);

  // ── Data ──
  const [months, setMonths] = useState<Record<string, MonthlyBucket>>({});
  const [warnings, setWarnings] = useState<CashflowWarning[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [consolidatedPOs, setConsolidatedPOs] = useState<PurchaseOrder[]>([]);
  const [inventoryOptions, setInventoryOptions] = useState<Array<{ id: string; name: string | null; skus: string[] }>>([]);

  // ── Loading ──
  const [isLoadingCashflow, setIsLoadingCashflow] = useState(false);
  const [isLoadingSuppliers, setIsLoadingSuppliers] = useState(false);
  const [isSavingSupplier, setIsSavingSupplier] = useState(false);
  const [isUpdatingPaidStatus, setIsUpdatingPaidStatus] = useState(false);

  // ── UI state ──
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [isSupplierDialogOpen, setIsSupplierDialogOpen] = useState(false);

  // ── Derived data ──
  const monthKeys = useMemo(() => getMonthRange(startMonth, endMonth), [startMonth, endMonth]);

  const availableBrands = useMemo(() => getUniqueBrands(months), [months]);

  // Filter months by selected suppliers, brands, and payment status (client-side)
  const filteredMonths = useMemo(() => {
    const hasFilters = selectedSupplierIds.length > 0 || selectedBrands.length > 0 || paymentStatusFilter !== 'all';
    if (!hasFilters) return months;

    const result: Record<string, MonthlyBucket> = {};
    for (const [key, month] of Object.entries(months)) {
      let entries = month.entries;

      if (selectedSupplierIds.length > 0) {
        entries = entries.filter(e => selectedSupplierIds.includes(e.supplierId));
      }
      if (selectedBrands.length > 0) {
        entries = entries.filter(e => e.brand && selectedBrands.includes(e.brand));
      }
      if (paymentStatusFilter === 'paid') {
        entries = entries.filter(e => e.paid === true);
      } else if (paymentStatusFilter === 'unpaid') {
        entries = entries.filter(e => !e.paid);
      }

      const total = entries.reduce((sum, e) => sum + e.trancheAmount, 0);
      const bySupplier: Record<string, number> = {};
      for (const e of entries) {
        bySupplier[e.supplierName] = (bySupplier[e.supplierName] || 0) + e.trancheAmount;
      }

      result[key] = { month: key, entries, total: Math.round(total * 100) / 100, bySupplier };
    }
    return result;
  }, [months, selectedSupplierIds, selectedBrands, paymentStatusFilter]);

  const incompletePONumbers = useMemo(() => {
    const set = new Set<string>();
    for (const w of warnings) {
      if (w.type === 'incomplete_po' && w.poNumber) {
        set.add(w.poNumber);
      }
    }
    return set;
  }, [warnings]);

  const selectedMonthEntries = useMemo(() => {
    if (!selectedMonth || !filteredMonths[selectedMonth]) return [];
    return filteredMonths[selectedMonth].entries;
  }, [filteredMonths, selectedMonth]);

  // Future allocations from today onward (component handles timeframe filtering)
  const futureAllocations = useMemo(() => {
    const today = moment().format('YYYY-MM-DD');
    const allEntries = Object.values(months).flatMap(m => m.entries);
    return allEntries
      .filter(e => e.paymentDate >= today)
      .sort((a, b) => a.paymentDate.localeCompare(b.paymentDate));
  }, [months]);

  // ── Data fetching ──
  const fetchCashflow = useCallback(async () => {
    setIsLoadingCashflow(true);
    const result = await cashflowService.getMonthlyBreakdown({
      startMonth,
      endMonth,
      status: statusFilter,
      brand: selectedBrands.length === 1 ? selectedBrands[0] : undefined,
    });

    if (result) {
      setMonths(result.months);
      setWarnings(result.warnings);
    }
    setIsLoadingCashflow(false);
  }, [startMonth, endMonth, statusFilter, selectedBrands]);

  const fetchSuppliers = useCallback(async () => {
    setIsLoadingSuppliers(true);
    const result = await cashflowService.getSuppliers();
    if (result) {
      setSuppliers(result.suppliers);
    }
    setIsLoadingSuppliers(false);
  }, []);

  const fetchPOsAndInventory = useCallback(async () => {
    // Fetch inventory options and all POs in parallel
    const [invResult, posResult] = await Promise.all([
      inventoryService.getCurrentInventory(),
      inventoryService.getIncomingPOs(),
    ]);

    if (invResult) {
      const records = Array.isArray(invResult) ? invResult : Object.values(invResult);
      const optionsMap = new Map<string, { id: string; name: string | null; skus: string[] }>();
      for (const item of records as any[]) {
        if (item?.inventory_id && !optionsMap.has(item.inventory_id)) {
          optionsMap.set(item.inventory_id, {
            id: item.inventory_id,
            name: item.inventory_name || null,
            skus: item.skus || [],
          });
        }
      }
      setInventoryOptions(Array.from(optionsMap.values()));
    }

    if (posResult) {
      const allPOs = (posResult as any).pos || [];
      const inventoryNames = new Map<string, string | null>();

      // Build name lookup from inventoryOptions
      for (const opt of inventoryOptions) {
        inventoryNames.set(opt.id, opt.name);
      }

      // API returns consolidated POs with lineItems — map to PurchaseOrder type
      const mapped: PurchaseOrder[] = allPOs.map((po: any) => ({
        poNumber: po.poNumber,
        arriveByDate: po.arriveByDate,
        description: po.description || "",
        lineItems: (po.lineItems || []).map((li: any) => ({
          inventoryId: li.inventoryId,
          inventoryName: inventoryNames.get(li.inventoryId) || li.inventoryName || undefined,
          sku: li.sku ?? null,
          quantity: li.quantity,
          unitPrice: li.unitPrice ?? null,
          manufacturerProductNumber: li.manufacturerProductNumber ?? null,
          skuConfirmedAt: li.skuConfirmedAt ?? null,
        })),
        ...(po.amount != null && { amount: po.amount }),
        ...(po.supplierId != null && { supplierId: po.supplierId }),
        ...(po.tariffRate != null && { tariffRate: po.tariffRate }),
        ...(po.status && { status: po.status }),
        ...(po.brand && { brand: po.brand }),
        ...(po.placementDate && { placementDate: po.placementDate }),
        ...(po.etd && { etd: po.etd }),
        ...(po.countryOfDestination && { countryOfDestination: po.countryOfDestination }),
        ...(po.paymentDateOverrides && { paymentDateOverrides: po.paymentDateOverrides }),
        ...(po.received != null && { received: po.received }),
        ...(po.paidTranches != null && { paidTranches: po.paidTranches }),
        ...(po.unitPrice != null && { unitPrice: po.unitPrice }),
        ...(po.priceType && { priceType: po.priceType }),
        ...(po.containerType && { containerType: po.containerType }),
      }));
      setConsolidatedPOs(mapped);
    }
  }, [inventoryOptions]);

  // Initial load
  useEffect(() => {
    fetchSuppliers();
    fetchPOsAndInventory();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch cashflow when filters change
  useEffect(() => {
    fetchCashflow();
  }, [fetchCashflow]);

  // ── Supplier handlers ──
  const handleCreateSupplier = async (data: any) => {
    setIsSavingSupplier(true);
    const result = await cashflowService.createSupplier(data);
    if (result) setSuppliers(result.suppliers);
    setIsSavingSupplier(false);
  };

  const handleUpdateSupplier = async (id: number, data: any) => {
    setIsSavingSupplier(true);
    const result = await cashflowService.updateSupplier(id, data);
    if (result) setSuppliers(result.suppliers);
    setIsSavingSupplier(false);
  };

  const handleDeleteSupplier = async (id: number) => {
    setIsSavingSupplier(true);
    const result = await cashflowService.deleteSupplier(id);
    if (result) {
      setSuppliers(prev => prev.filter(s => s.id !== id));
    }
    setIsSavingSupplier(false);
  };

  // ── SKU master prices (pre-fill for PO line unit prices) ──
  const [catalogSuggestions, setCatalogSuggestions] = useState<CatalogSuggestions | null>(null);
  const [priceChangesToOffer, setPriceChangesToOffer] = useState<{ poNumber: string; changes: PriceChange[]; productNumberChanges: ProductNumberChange[] } | null>(null);
  const [generatingPdfPoNumber, setGeneratingPdfPoNumber] = useState<string | null>(null);

  useEffect(() => {
    let isCancelled = false;
    inventoryService.getCatalogPrices().then(response => {
      if (isCancelled || !response) return;
      setCatalogSuggestions(response);
    });
    return () => { isCancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSuggestPoNumber = async () => {
    const suggestion = await inventoryService.getNextPoNumber(pom.editingPOCountryOfDestination);
    if (suggestion) {
      pom.dialogProps.onSetPONumber(suggestion.poNumber);
    }
  };

  const [pdfError, setPdfError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleGeneratePdf = async (poToPrint: PurchaseOrder) => {
    setGeneratingPdfPoNumber(poToPrint.poNumber);
    try {
      const blob = await inventoryService.downloadPoPdf(poToPrint.poNumber);
      if (blob) {
        downloadBlob(blob, `${poToPrint.poNumber.replace(/[^A-Za-z0-9._-]/g, "_")}.pdf`);
      } else {
        setPdfError(`Failed to generate the PO document for ${poToPrint.poNumber}`);
      }
    } finally {
      setGeneratingPdfPoNumber(null);
    }
  };

  // ── PO management hook ──
  const pom = usePOManagement({
    inventoryOptions,
    suppliers,
    defaultNewPODate: moment().format("YYYY-MM-DD"),
    onSave: async (params) => {
      const posToSave = params.lineItems.map(li => ({
        inventoryId: li.inventoryId,
        poNumber: params.poNumber,
        arriveByDate: params.arriveByDate,
        quantity: li.quantity,
        description: params.description,
        placementDate: params.placementDate,
        etd: params.etd,
        amount: params.amount,
        supplierId: params.supplierId,
        tariffRate: li.tariffRate ?? null,
        status: params.status,
        brand: params.brand,
        countryOfDestination: params.countryOfDestination,
        paymentDateOverrides: params.paymentDateOverrides,
        unitPrice: li.unitPrice ?? null,
        priceType: params.priceType,
        containerType: params.containerType,
        shipToFcId: params.shipToFcId,
        manufacturerProductNumber: li.manufacturerProductNumber ?? null,
        skuConfirmed: Boolean(li.skuConfirmed ?? li.skuConfirmedAt),
      }));

      try {
        await persistPO(inventoryService, params, posToSave);
      } catch (error: any) {
        setSaveError(`PO ${params.poNumber} was not saved: ${error?.message || error}`);
        await fetchPOsAndInventory();
        return;
      }
      pom.markSaved();
      const changes = findPriceChanges(params.lineItems, params.containerType, catalogSuggestions);
      const productNumberChanges = findProductNumberChanges(params.lineItems, catalogSuggestions);
      if (changes.length > 0 || productNumberChanges.length > 0) setPriceChangesToOffer({ poNumber: params.poNumber, changes, productNumberChanges });
      await fetchPOsAndInventory();
      await fetchCashflow();
    },
    onDelete: async (poToDelete) => {
      for (const li of poToDelete.lineItems) {
        await inventoryService.deleteIncomingPO(li.inventoryId, poToDelete.poNumber);
      }
      await fetchPOsAndInventory();
      await fetchCashflow();
    },
  });

  // Update local paid state after server confirms — no refetch needed
  const applyPaidTranchesLocally = (poNumber: string, paidTranches: Record<string, boolean> | null) => {
    setConsolidatedPOs(prev =>
      prev.map(po => po.poNumber === poNumber ? { ...po, paidTranches } : po)
    );
    setMonths(prev => {
      const updated: Record<string, MonthlyBucket> = {};
      for (const [key, month] of Object.entries(prev)) {
        const entries = month.entries.map(e => {
          if (e.poNumber !== poNumber) return e;
          const isPaid = !!(paidTranches && paidTranches[String(e.trancheOrder)]);
          return e.paid === isPaid ? e : { ...e, paid: isPaid };
        });
        updated[key] = entries === month.entries ? month : {
          ...month,
          entries,
        };
      }
      return updated;
    });
  };

  const handleBulkMarkAllPaid = async (updates: Array<{ poNumber: string; paidTranches: Record<string, boolean> }>) => {
    setIsUpdatingPaidStatus(true);
    try {
      await Promise.all(
        updates.map(({ poNumber, paidTranches }) =>
          inventoryService.updatePaidTranches(poNumber, paidTranches)
        )
      );
      for (const { poNumber, paidTranches } of updates) {
        applyPaidTranchesLocally(poNumber, paidTranches);
      }
    } finally {
      setIsUpdatingPaidStatus(false);
    }
  };

  const handleBulkClearPaid = async (poNumbers: string[]) => {
    setIsUpdatingPaidStatus(true);
    try {
      await inventoryService.clearPaidTranches(poNumbers);
      for (const poNumber of poNumbers) {
        applyPaidTranchesLocally(poNumber, null);
      }
    } finally {
      setIsUpdatingPaidStatus(false);
    }
  };

  const handleToggleAllocationPaid = async (poNumber: string, trancheOrder: number, paid: boolean) => {
    const po = consolidatedPOs.find(p => p.poNumber === poNumber);
    const current = po?.paidTranches || {};
    const updated = { ...current, [String(trancheOrder)]: paid };
    setIsUpdatingPaidStatus(true);
    try {
      await inventoryService.updatePaidTranches(poNumber, updated);
      applyPaidTranchesLocally(poNumber, updated);
    } finally {
      setIsUpdatingPaidStatus(false);
    }
  };

  const handleRefresh = () => {
    fetchCashflow();
    fetchPOsAndInventory();
  };

  const handlePOClickFromTable = (poNumber: string) => {
    const found = consolidatedPOs.find(p => p.poNumber === poNumber);
    if (found) {
      pom.openEditPO(found);
      pom.openDialog(found);
    }
  };

  const handleOpenIncompletePOs = () => {
    pom.setPOTabValue("incomplete");
    pom.cancelEdit();
    pom.openDialog();
  };

  const handleOpenWarningPO = (poNumber: string) => {
    const found = consolidatedPOs.find(p => p.poNumber === poNumber);
    if (found) {
      pom.openDialog(found);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
    <Box sx={{ p: 0 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Cashflow Analysis
      </Typography>

      {pdfError && (
        <Alert severity="error" onClose={() => setPdfError(null)} sx={{ mb: 2 }}>
          {pdfError}
        </Alert>
      )}
      {saveError && (
        <Alert severity="error" onClose={() => setSaveError(null)} sx={{ mb: 2 }}>
          {saveError}
        </Alert>
      )}

      {/* Warnings */}
      <CashflowWarningsSection
        warnings={warnings}
        consolidatedPOs={consolidatedPOs}
        futureAllocations={futureAllocations}
        onOpenIncompletePOs={handleOpenIncompletePOs}
        onOpenWarningPO={handleOpenWarningPO}
      />

      {/* Control Panel */}
      <CashflowControlPanel
        startMonth={startMonth}
        endMonth={endMonth}
        statusFilter={statusFilter}
        paymentStatusFilter={paymentStatusFilter}
        viewMode={viewMode}
        chartStackingMode={chartStackingMode}
        selectedBrands={selectedBrands}
        selectedSupplierIds={selectedSupplierIds}
        availableBrands={availableBrands}
        suppliers={suppliers}
        onStartMonthChange={setStartMonth}
        onEndMonthChange={setEndMonth}
        onStatusFilterChange={setStatusFilter}
        onPaymentStatusFilterChange={setPaymentStatusFilter}
        onViewModeChange={setViewMode}
        onChartStackingModeChange={setChartStackingMode}
        onSelectedBrandsChange={setSelectedBrands}
        onSelectedSupplierIdsChange={setSelectedSupplierIds}
        onManageSuppliers={() => setIsSupplierDialogOpen(true)}
        onManagePOs={() => {
          pom.cancelEdit();
          pom.openDialog();
        }}
        onRefresh={handleRefresh}
      />

      {/* Loading state */}
      {isLoadingCashflow ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Bar Chart */}
          <CashflowBarChart
            months={filteredMonths}
            monthKeys={monthKeys}
            chartStackingMode={chartStackingMode}
            onMonthClick={setSelectedMonth}
          />

          {/* Monthly Comparison Table */}
          <MonthlyComparisonTable
            months={filteredMonths}
            monthKeys={monthKeys}
            viewMode={viewMode}
            onMonthClick={setSelectedMonth}
            onPOClick={handlePOClickFromTable}
          />

          {/* Monthly Detail Table (when a month is selected) */}
          {selectedMonth && selectedMonthEntries.length > 0 && (
            <MonthlyDetailTable
              monthKey={selectedMonth}
              entries={selectedMonthEntries}
              onTogglePaid={handleToggleAllocationPaid}
              isUpdatingPaid={isUpdatingPaidStatus}
            />
          )}
        </>
      )}

      {/* Supplier Management Dialog */}
      <SupplierManagementDialog
        isOpen={isSupplierDialogOpen}
        onClose={() => setIsSupplierDialogOpen(false)}
        suppliers={suppliers}
        onCreateSupplier={handleCreateSupplier}
        onUpdateSupplier={handleUpdateSupplier}
        onDeleteSupplier={handleDeleteSupplier}
        isSaving={isSavingSupplier}
      />

      {/* PO Management Dialog (shared, with cashflow mode) */}
      <SavePricesToSkuMasterDialog
        open={priceChangesToOffer != null}
        onClose={() => setPriceChangesToOffer(null)}
        poNumber={priceChangesToOffer?.poNumber ?? ""}
        changes={priceChangesToOffer?.changes ?? []}
        productNumberChanges={priceChangesToOffer?.productNumberChanges ?? []}
        onSaved={() => inventoryService.getCatalogPrices().then(result => { if (result) setCatalogSuggestions(result); })}
      />

      <POManagementDialog
        {...pom.dialogProps}
        consolidatedPOs={consolidatedPOs}
        incompletePONumbers={incompletePONumbers}
        catalogSuggestions={catalogSuggestions}
        onSuggestPONumber={handleSuggestPoNumber}
        onGeneratePdf={handleGeneratePdf}
        generatingPdfPoNumber={generatingPdfPoNumber}
        onBulkMarkAllPaid={handleBulkMarkAllPaid}
        onBulkClearPaid={handleBulkClearPaid}
        onTogglePaidTranche={handleToggleAllocationPaid}
        isUpdatingPaidStatus={isUpdatingPaidStatus}
      />

      {/* Delete PO Confirmation */}
      <DeletePOConfirmation
        poToDelete={pom.poToDelete}
        isDeletingPO={pom.isDeletingPO}
        onConfirm={pom.confirmDeletePO}
        onCancel={pom.cancelDeletePO}
      />
    </Box>
    </LocalizationProvider>
  );
}
